
import React from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { saveRechargeToSupabase } from '@/lib/supabase';
import { useTranslation } from 'react-i18next';
import { useOnboardingContext } from '@/context/OnboardingContext';
import { formatUssdTelUrl } from '@/utils/ussd-utils';

interface RechargeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RechargeDialog: React.FC<RechargeDialogProps> = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const { userData } = useOnboardingContext();

  const rechargeFormSchema = z.object({
    cardNumbers: z.string()
      .min(14, t('tools.validation.cardLength'))
      .regex(/^\d+$/, t('tools.validation.cardFormat')),
    recipientNumber: z.string()
      .min(8, t('tools.validation.phoneLength'))
      .regex(/^77\d{6}$/, t('tools.validation.phoneFormat'))
  });

  const rechargeForm = useForm<z.infer<typeof rechargeFormSchema>>({
    resolver: zodResolver(rechargeFormSchema),
    defaultValues: {
      cardNumbers: "",
      recipientNumber: ""
    }
  });

  const onRechargeSubmit = (data: z.infer<typeof rechargeFormSchema>) => {
    const recharges = JSON.parse(localStorage.getItem('recharges') || '[]');
    const newRecharge = {
      recipientNumber: data.recipientNumber,
      cardNumbers: `****${data.cardNumbers.slice(-4)}`,
      date: new Date().toISOString()
    };
    
    recharges.push(newRecharge);
    localStorage.setItem('recharges', JSON.stringify(recharges));
    
    // Save to the rechargeCards array for the history tab
    const rechargeCards = JSON.parse(localStorage.getItem('rechargeCards') || '[]');
    const newRechargeCard = {
      recipientNumber: data.recipientNumber,
      cardNumbers: `****${data.cardNumbers.slice(-4)}`,
      date: new Date().toISOString()
    };
    
    rechargeCards.push(newRechargeCard);
    localStorage.setItem('rechargeCards', JSON.stringify(rechargeCards));
    
    try {
      saveRechargeToSupabase(newRecharge, userData?.email);
      console.log('Recharge saved to Supabase');
    } catch (error) {
      console.error('Failed to save recharge to Supabase:', error);
    }
    
    // Construct and navigate to tel URL for recharge with encoded USSD
    window.location.href = formatUssdTelUrl(`*166#${data.recipientNumber}*${data.cardNumbers}#`);
    
    onOpenChange(false);
    rechargeForm.reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('tools.recharge.dialogTitle')}</DialogTitle>
        </DialogHeader>
        <Form {...rechargeForm}>
          <form onSubmit={rechargeForm.handleSubmit(onRechargeSubmit)} className="space-y-4">
            <FormField
              control={rechargeForm.control}
              name="cardNumbers"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input 
                      placeholder={t('tools.recharge.cardPlaceholder')} 
                      {...field} 
                      type="text"
                      pattern="[0-9]*"
                      inputMode="numeric"
                      className="text-lg"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={rechargeForm.control}
              name="recipientNumber"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input 
                      placeholder={t('tools.recharge.recipientPlaceholder')} 
                      {...field} 
                      type="tel"
                      pattern="[0-9]*"
                      inputMode="numeric"
                      className="text-lg"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit">{t('common.continue')}</Button>
            </DialogFooter>
          </form>
        </Form>
        <p className="text-sm text-muted-foreground mt-2">
          {t('tools.recharge.hint')}
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default RechargeDialog;
